import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine} from '@dropbox/dig-icons/assets';
import {Link} from 'components/DSYS/Link';
import {MouseEvent} from 'react';
import {
  dashboardLinkDestination,
  logDashboardInteraction,
} from 'views/home/dashboard/dashboardAnalytics';
import {useDashboardTileId} from 'views/home/dashboard/DashboardTileContext';

/**
 * Shared "View all" control, styled the way Apple renders "See All": a plain
 * tinted text link (no button chrome / background pill), regular weight, small
 * size, with a quiet trailing chevron. It's a real link, so cmd/ctrl/middle-click
 * opens a new tab. `Text color="inherit"` + an uncolored icon both pick up the
 * link's accent color from the anchor.
 *
 * Pass `count` only when the tile shows the full set and the number is
 * meaningful. Omit it when the underlying list is itself capped (e.g. news),
 * since a partial count like "View all 5" would understate what's available.
 */
type ViewAllLinkProps = {
  count?: number;
  to: string;
  /** For hosts outside the dashboard, which log their own click. */
  onClick?: () => void;
  /** Router state for the destination, e.g. viaState() for /workstreams. */
  state?: Record<string, unknown>;
};

const ViewAllLink = ({count, to, onClick, state}: ViewAllLinkProps) => {
  const tileId = useDashboardTileId();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (tileId) {
      logDashboardInteraction({
        action: 'header_link_click',
        tileId,
        target: 'view_all',
        destination: dashboardLinkDestination(to),
      });
    }
    onClick?.();
    event.stopPropagation();
  };

  return (
    <Text size="xsmall" color="faint">
      <Link
        to={to}
        state={state}
        hasNoUnderline
        showUnderlineOnHover
        monochromatic
        style={{alignSelf: 'flex-start'}}
        onClick={handleClick}
      >
        <Box display="flex" alignItems="center" style={{gap: 2}}>
          {count !== undefined ? `View all ${count}` : 'View all'}
          <UIIcon src={ChevronRightLine} size="small" />
        </Box>
      </Link>
    </Text>
  );
};

/**
 * "View all" affordance for a tile's header (top-right, Apple "See All" style).
 * Preferred placement: it stays in the same spot on every tile and never leaves
 * an orphan gap between the list and the link.
 */
export const TileHeaderLink = (props: ViewAllLinkProps) => <ViewAllLink {...props} />;

/**
 * "View all" affordance shown directly below the last list row. Use only when
 * the header can't carry the link (e.g. it already holds another control).
 */
export const TileListFooter = (props: ViewAllLinkProps) => <ViewAllLink {...props} />;
